import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Edit3, LogOut, Plus, Save, Trash2, Upload, X } from "lucide-react";

const ADMIN_USER = "Abdulrahman";
const ADMIN_PASSWORD = "AbdulPHD";
const ADMIN_AUTH_KEY = "abdulrahman-admin-unlocked";

const emptyForm = {
  id: "",
  collection: "pipeline",
  title: "",
  year: "",
  location: "",
  category: "Visuals",
  description: "",
  details: "",
  imageUrls: "",
};

const collectionLabels = {
  pipeline: "Project Pipelines",
  built: "Built Projects",
  archive: "Archive",
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const normalizeProjectForForm = (project) => ({
  id: project.id || "",
  collection: project.collection || "archive",
  title: project.title || project.label || "",
  year: project.year || "",
  location: project.location || "",
  category: project.category || project.tag || "Visuals",
  description: project.description || "",
  details: Array.isArray(project.details) ? project.details.join("\n") : "",
  imageUrls: (project.images || [project.image || project.img].filter(Boolean)).join("\n"),
});

export const AdminProjectModal = ({
  open,
  onClose,
  onSave,
  onDownload,
  onDelete,
  projectCount = 0,
  projects = [],
}) => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [login, setLogin] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setIsUnlocked(localStorage.getItem(ADMIN_AUTH_KEY) === "true");
  }, [open]);

  const editingExisting = Boolean(form.id);
  const sortedProjects = useMemo(() => {
    const order = { pipeline: 0, built: 1, archive: 2 };
    return [...projects].sort((a, b) => (order[a.collection] ?? 9) - (order[b.collection] ?? 9) || a.title.localeCompare(b.title));
  }, [projects]);

  const update = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const handleLogin = (event) => {
    event.preventDefault();
    if (login.username.trim() === ADMIN_USER && login.password === ADMIN_PASSWORD) {
      localStorage.setItem(ADMIN_AUTH_KEY, "true");
      setIsUnlocked(true);
      setLoginError("");
      return;
    }
    setLoginError("Wrong username or password.");
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_AUTH_KEY);
    setIsUnlocked(false);
    setForm(emptyForm);
    setFiles([]);
  };

  const handleEdit = (project) => {
    setForm(normalizeProjectForForm(project));
    setFiles([]);
    setError("");
  };

  const handleNew = () => {
    setForm(emptyForm);
    setFiles([]);
    setError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError("Add at least a title and description.");
      return;
    }

    const uploadedImages = await Promise.all(files.map(fileToDataUrl));
    const linkedImages = form.imageUrls
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    const images = [...uploadedImages, ...linkedImages];
    const id = form.id || `custom-${form.collection}-${slug(form.title)}-${Date.now()}`;

    onSave({
      id,
      collection: form.collection,
      title: form.title.trim(),
      label: form.title.trim(),
      year: form.year.trim() || "Draft",
      location: form.location.trim(),
      category: form.category.trim() || "Visuals",
      tag: form.category.trim() || "Visuals",
      description: form.description.trim(),
      details: form.details
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
      image: images[0],
      img: images[0],
      images,
      annotation: editingExisting ? "edited entry" : "new entry",
    });

    setForm(emptyForm);
    setFiles([]);
    setError("");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, pointerEvents: "auto" }}
          animate={{ opacity: 1, pointerEvents: "auto" }}
          exit={{ opacity: 0, pointerEvents: "none", transition: { duration: 0.15 } }}
          onClick={onClose}
          className="fixed inset-0 z-[120] grid place-items-center px-4"
          style={{ background: "rgba(40,35,25,0.45)", backdropFilter: "blur(3px)" }}
        >
          <motion.div
            onClick={(event) => event.stopPropagation()}
            initial={{ opacity: 0, scale: 0.94, y: 18, rotate: -1 }}
            animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ type: "spring", stiffness: 200, damping: 22 }}
            className="paper paper-corner-fold project-modal-scroll relative w-full max-w-5xl max-h-[88vh] p-7 paper-scroll"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close admin form"
              className="absolute top-4 right-4 h-8 w-8 grid place-items-center rounded-full text-ink-faint hover:text-accentRed hover:bg-[var(--bg-board-2)] transition"
            >
              <X size={16} />
            </button>

            {!isUnlocked ? (
              <form onSubmit={handleLogin} className="mx-auto max-w-sm py-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-faint">Locked admin</p>
                <h2 className="font-display text-2xl font-medium tracking-tight text-ink mt-1">Sign in to edit</h2>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">
                  Use the private username and password before editing projects.
                </p>
                <div className="mt-6 space-y-4">
                  <Field label="Username" value={login.username} onChange={(event) => setLogin((current) => ({ ...current, username: event.target.value }))} required />
                  <Field label="Password" type="password" value={login.password} onChange={(event) => setLogin((current) => ({ ...current, password: event.target.value }))} required />
                </div>
                {loginError && <p className="mt-3 font-mono text-[11px] text-accentRed">{loginError}</p>}
                <button
                  type="submit"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-3 py-3 bg-accentRed text-paper hover:bg-ink transition font-mono text-[10px] uppercase tracking-[0.22em]"
                >
                  Unlock editor
                </button>
              </form>
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-faint">Hidden editor</p>
                    <h2 className="font-display text-2xl font-medium tracking-tight text-ink mt-1">
                      {editingExisting ? "Edit project" : "Add project"}
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm text-ink-soft leading-relaxed">
                      Changes appear immediately in this browser. To make them permanent on GitHub, download the JSON file and upload it to
                      <span className="font-mono text-[11px]"> frontend/public/assets/custom-projects.json</span>.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="inline-flex items-center gap-2 border border-[var(--paper-edge)] px-3 py-2 text-ink-soft hover:border-ink/50 hover:text-ink transition font-mono text-[10px] uppercase tracking-[0.18em]"
                  >
                    <LogOut size={13} /> Logout
                  </button>
                </div>

                <div className="mt-6 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
                  <form onSubmit={handleSave}>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Title" value={form.title} onChange={update("title")} required />
                      <Field label="Year" value={form.year} onChange={update("year")} />
                      <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-1">Section</label>
                        <select value={form.collection} onChange={update("collection")} className="w-full bg-transparent border-b border-[var(--paper-edge)] focus:border-ink outline-none text-sm text-ink py-1.5">
                          <option value="pipeline">Project Pipelines</option>
                          <option value="built">Built Projects</option>
                          <option value="archive">Archive</option>
                        </select>
                      </div>
                      <Field label="Category / Type" value={form.category} onChange={update("category")} />
                      <Field label="Location" value={form.location} onChange={update("location")} />
                      <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-1">
                          Upload images
                        </label>
                        <label className="flex items-center justify-center gap-2 border border-dashed border-[var(--paper-edge)] px-3 py-3 text-sm text-ink-soft hover:border-ink/50 transition">
                          <Upload size={14} />
                          {files.length ? `${files.length} file(s) selected` : "Choose image files"}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(event) => setFiles(Array.from(event.target.files || []))}
                            className="sr-only"
                          />
                        </label>
                      </div>
                    </div>

                    <div className="mt-4 space-y-4">
                      <Textarea label="Description" rows={4} value={form.description} onChange={update("description")} required />
                      <Textarea label="Details, one per line" rows={3} value={form.details} onChange={update("details")} />
                      <Textarea label="Image URLs or existing image data, one per line" rows={2} value={form.imageUrls} onChange={update("imageUrls")} />
                    </div>

                    {error && <p className="mt-3 font-mono text-[11px] text-accentRed">{error}</p>}

                    <div className="mt-5 grid sm:grid-cols-2 gap-3">
                      <button
                        type="submit"
                        className="inline-flex items-center justify-center gap-2 px-3 py-3 bg-accentRed text-paper hover:bg-ink transition font-mono text-[10px] uppercase tracking-[0.22em]"
                      >
                        <Save size={13} /> {editingExisting ? "Save edit" : "Add project"}
                      </button>
                      <button
                        type="button"
                        onClick={handleNew}
                        className="inline-flex items-center justify-center gap-2 px-3 py-3 border border-[var(--paper-edge)] text-ink hover:border-ink hover:bg-[var(--bg-board-2)] transition font-mono text-[10px] uppercase tracking-[0.22em]"
                      >
                        <Plus size={13} /> New blank project
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={onDownload}
                      disabled={!projectCount}
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-3 border border-ink text-ink hover:bg-ink hover:text-paper transition font-mono text-[10px] uppercase tracking-[0.22em] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink"
                    >
                      <Download size={13} /> Download permanent JSON ({projectCount})
                    </button>
                  </form>

                  <div className="border-t lg:border-t-0 lg:border-l border-dashed border-[var(--paper-edge)] pt-5 lg:pt-0 lg:pl-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-2">Edit or delete project</p>
                    <div className="max-h-[520px] space-y-2 overflow-y-auto paper-scroll pr-1">
                      {sortedProjects.map((project) => (
                        <div key={project.id} className="border border-[var(--paper-edge)] px-3 py-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm text-ink">{project.title}</p>
                            <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-ink-faint">
                              {collectionLabels[project.collection] || project.collection}{project.custom ? " - edited/new" : ""}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => handleEdit(project)}
                              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink hover:text-accentRed transition"
                            >
                              <Edit3 size={12} /> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(project)}
                              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-accentRed hover:text-ink transition"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, value, onChange, required, type = "text" }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-1">
      {label}{required ? " *" : ""}
    </label>
    <input
      value={value}
      required={required}
      type={type}
      onChange={onChange}
      className="w-full bg-transparent border-b border-[var(--paper-edge)] focus:border-ink outline-none text-sm text-ink py-1.5"
    />
  </div>
);

const Textarea = ({ label, value, onChange, rows, required }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-1">
      {label}{required ? " *" : ""}
    </label>
    <textarea
      value={value}
      required={required}
      rows={rows}
      onChange={onChange}
      className="w-full bg-transparent border border-[var(--paper-edge)] focus:border-ink outline-none text-sm text-ink p-2 resize-y"
    />
  </div>
);

export default AdminProjectModal;
